<template>
  <ModalBase :title="title" :open="isOpen" :icon="icon" @close="closeModal">
    <template #content>
      <slot name="content" />
    </template>
    <template #footer>
      <slot name="footer" />
      <div class="footer-buttons">
        <ButtonOutline :buttonText="t('general__text__cancel')" @onClicked="closeModal" />
        <ButtonPrimary
          :buttonText="submitButtonText"
          :disabled="disableSubmit"
          @onClicked="submit"
        />
      </div>
    </template>
  </ModalBase>
</template>

<script setup lang="ts">
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ModalBase from './ModalBase.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'

type Props = {
  title: string
  submitButtonText?: string
  disableSubmit?: boolean
  open?: boolean
  icon?: string
}

const props = withDefaults(defineProps<Props>(), {
  submitButtonText: 'Submit',
  disableSubmit: false,
  open: false,
  icon: undefined
})

const { title, submitButtonText, disableSubmit } = toRefs(props)

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'close'): void
}>()

const { t } = useI18n()

const isOpen = ref(props.open)

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) return
  if (event.key === 'Enter') {
    submit()
  }
}

const closeModal = () => {
  isOpen.value = false
  emit('close')
}

const submit = () => {
  if (!disableSubmit.value) {
    isOpen.value = false
    emit('submit')
  }
}
</script>

<style scoped lang="scss">
.footer-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  gap: 8px;
}
</style>
